import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Form,
  Row,
  Col,
  Table,
  DatePicker,
  InputNumber,
  Select,
  Input,
  Modal,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import CustomLayout from "../../Layout/CustomLayout";
import PharmacySideMenu from "../../Layout/CustomLayout/PharmacySideMenu";
import { UiButton, UiTag, ActionIconButton } from "../../ui";
import {
  ListScreen,
  DetailHead,
  DetailGrid,
  DetailFull,
  DetailCard,
  DetailBody,
  DetailEmpty,
  CardNote,
  FootTotal,
  BillTotals,
  AddLineButton,
  IconAction,
  RowActions,
  listTableClass,
} from "../common/ListScreen";
import CgIcon from "../common/CgIcon";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import GstSummaryTable from "../common/GstSummaryTable";
import { fetchActivePharmacyMedicines } from "../common/medicineSearch";
import { formatAmount } from "../common/money";
import { notPositiveError, MONEY_MIN } from "../common/validators";
import { getPharmacyConfigListApi } from "../../../services/pharmacyConfigs.services";
import {
  getPharmacyPurchaseReturnApi,
  createPharmacyPurchaseReturnApi,
  completePharmacyPurchaseReturnApi,
  cancelPharmacyPurchaseReturnApi,
  editPharmacyPurchaseReturnApi,
  addPharmacyPurchaseReturnEditItemApi,
  updatePharmacyPurchaseReturnEditItemApi,
  removePharmacyPurchaseReturnEditItemApi,
} from "../../../services/pharmacyPurchaseReturns.services";
import { getPharmacyMedicineBatchesApi } from "../../../services/pharmacyMedicines.services";
import { getErrorMessage } from "../../../helpers/error";
import PharmacyGlobalSearch from "../common/PharmacyGlobalSearch";
import AuditLogDrawer, { AUDIT_ENTITIES } from "../common/AuditLogDrawer";
import LeaveWithoutSavingModal from "../common/LeaveWithoutSavingModal";
import {
  canCancelDocument,
  canEditDocument,
  cancelFailureMessage,
  purchaseFailureMessage,
} from "../common/cancelWindow";
import { useIsPharmacyAdmin } from "../Users/PharmacyUsers";

const LIST_PATH = "/pharmacy/purchase-return";

const footerActionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: 12,
};

const fetchVendors = (text) =>
  getPharmacyConfigListApi(
    "vendors",
    new URLSearchParams({ search: text || "", limit: 50 })
  ).then((res) =>
    (res?.data?.results || []).map((item) => ({ value: item.id, label: item.name }))
  );

let rowSeq = 0;
const newRow = () => ({
  key: `pr-${(rowSeq += 1)}`,
  medicine: undefined,
  medicine_name: "",
  batch: undefined,
  batches: [],
  loadingBatches: false,
  qty: null,
  rate: null,
  gst_percent: 0,
});

/**
 * A line's money figures are derived from qty, rate, discount and GST rather
 * than entered, so the totals cannot disagree with the lines they came from.
 */
const deriveLine = (row) => {
  const qty = Number(row?.qty || 0);
  const rate = Number(row?.rate || 0);
  const gross = qty * rate;
  const taxable = gross;
  const gstPercent = Number(row?.gst_percent || 0);
  // Intra-state: GST splits evenly between SGST and CGST.
  const half = (taxable * gstPercent) / 200;
  return {
    ...row,
    taxable_amount: Number(taxable.toFixed(2)),
    sgst: Number(half.toFixed(2)),
    cgst: Number(half.toFixed(2)),
    igst: 0,
    amount: Number((taxable + half * 2).toFixed(2)),
  };
};

/*
 * A saved line, in the shape the grid reads and types into. The batch it went
 * out on is seeded as the only option so the select shows what was picked -
 * choosing a different medicine fetches that medicine's batches as usual.
 */
const itemToRow = (item, index) => ({
  key: `saved-${item.id ?? index}`,
  itemId: item.id,
  medicine: item.pharmacy_medicine ?? item.medicine,
  medicine_name: item.medicine_name || item.pharmacy_medicine_name || "",
  batch: item.batch,
  batch_number: item.batch_number,
  batches: item.batch
    ? [
        {
          id: item.batch,
          batch_number: item.batch_number,
          expiry: item.expiry,
          available_qty: item.available_qty,
        },
      ]
    : [],
  loadingBatches: false,
  qty: Number(item.qty) || 0,
  rate: Number(item.rate) || 0,
  gst_percent: Number(item.gst_percentage) || 0,
  /* What the server holds for this line, kept so an untouched line prints the
     server's own figures rather than this screen's arithmetic on them. */
  taxable_amount: Number(item.qty || 0) * Number(item.rate || 0),
  sgst: Number(item.gst_amount || 0) / 2,
  cgst: Number(item.gst_amount || 0) / 2,
  igst: 0,
  amount: Number(item.amount) || 0,
});

const lineFigures = (row) =>
  row.itemId && !row.touched
    ? {
        ...row,
        taxable_amount: Number(row.taxable_amount) || 0,
        sgst: Number(row.sgst) || 0,
        cgst: Number(row.cgst) || 0,
        igst: 0,
        amount: Number(row.amount) || 0,
      }
    : deriveLine(row);

const PurchaseReturnPage = () => {
  const { returnId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !returnId || returnId === "new";

  /*
   * Set when the return was started from the near-expiry suggestions (see
   * RaisePurchaseReturnModal): vendor, reason and the batch lines are already
   * known, and the pharmacist edits them from here like any other draft.
   */
  const prefill = isNew ? location.state?.purchaseReturnPrefill : null;

  const [form] = Form.useForm();
  const [entry, setEntry] = useState(null);
  /*
   * A return opens with no lines, the way a purchase entry does - the empty
   * state offers the first one rather than a blank row sitting there asking to
   * be filled in.
   */
  const [rows, setRows] = useState(() => prefill?.rows || []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  /* Leaving with a return that has not been raised yet asks first. */
  const [leaveOpen, setLeaveOpen] = useState(false);
  /* What has happened to this return, read beside it - Pharmacy Admins only,
     which is who the endpoint answers. */
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (isNew) return;
    /* The document in hand belongs to the last id this screen was opened on.
       One route serves them all, so moving from one to another changes the
       parameter without remounting - and the previous one sits in state, whole
       and wrong, until the new one lands. A refresh of the same id is not that
       and must not blank the screen. */
    setEntry((prev) => (String(prev?.id) === String(returnId) ? prev : null));
    setLoading(true);
    getPharmacyPurchaseReturnApi(returnId)
      .then((res) => {
        const data = res?.data;
        setEntry(data);
        form.setFieldsValue({
          vendor: data?.vendor,
          return_date: data?.return_date ? dayjs(data.return_date) : null,
          reason: data?.reason,
        });
        /* The return's own lines are the table's rows from here - read as they
           stand, and typed into one at a time when one is being edited. */
        setRows((data?.items || []).map(itemToRow));
      })
      .catch((err) => message.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [returnId, isNew, form]);

  useEffect(() => {
    if (!prefill?.vendor) return;
    form.setFieldsValue({ vendor: prefill.vendor, reason: prefill.reason });
  }, [prefill?.vendor, prefill?.reason, form]);

  const isPharmacyAdmin = useIsPharmacyAdmin();
  /*
   * Editing is a line at a time rather than the whole return at once: the
   * pencil at the end of a line opens that line and nothing else, which is how
   * a return is actually corrected - one quantity, one batch. The header above
   * is a matter of record once the return is raised.
   */
  const [editingKey, setEditingKey] = useState(null);
  /* The line as it was when it was opened, so the cross can put it back
     without disturbing anything else that has been typed. */
  const [rowBackup, setRowBackup] = useState(null);
  /*
   * Lines taken off the table that the server still holds.
   *
   * Taking a line off the grid is not telling the server about it - the
   * footer does that - so the ids are kept here until it is pressed, and each
   * one is withdrawn by name rather than by being left out of a list.
   */
  const [removedItemIds, setRemovedItemIds] = useState([]);
  /* Whether the table says something the server has not been told. Nothing
     goes up until the footer is pressed, so this is what the footer is for. */
  const [dirty, setDirty] = useState(false);
  const readOnly = Boolean(entry?.id);
  /*
   * What may still be rewritten, on the same rule that governs calling it off:
   * a draft any time, a completed one inside the day it was submitted, and a
   * Pharmacy Admin whenever. A cancelled one, never.
   */
  const linesEditable =
    isNew || canEditDocument(entry, { isAdmin: isPharmacyAdmin });
  const isEditingRow = (row) => isNew || editingKey === row.key;

  const startRowEdit = (row) => {
    setRowBackup({ key: row.key, row });
    setEditingKey(row.key);
  };

  /* The tick: what was typed stays on the table, and the line closes. It is
     still only in the browser - the footer is what writes it. */
  const commitRowEdit = () => {
    setRowBackup(null);
    setEditingKey(null);
  };

  /* The cross: this line goes back to what it was, and nothing else moves. */
  const revertRowEdit = () => {
    if (rowBackup) {
      setRows((prev) =>
        prev.map((r) => (r.key === rowBackup.key ? rowBackup.row : r))
      );
    } else if (editingKey) {
      setRows((prev) => prev.filter((r) => r.key !== editingKey));
    }
    setRowBackup(null);
    setEditingKey(null);
  };

  /* Everything back to what the server holds. */
  const discardChanges = () => {
    setRows((entry?.items || []).map(itemToRow));
    setRemovedItemIds([]);
    setRowBackup(null);
    setEditingKey(null);
    setDirty(false);
  };

  const lines = useMemo(
    () => rows.filter((r) => r.batch).map(lineFigures),
    [rows]
  );

  const totals = useMemo(() => {
    const taxable = lines.reduce((s, l) => s + Number(l.taxable_amount || 0), 0);
    const discount = lines.reduce((s, l) => s + Number(l.discount || 0), 0);
    const gst = lines.reduce(
      (s, l) => s + Number(l.sgst || 0) + Number(l.cgst || 0) + Number(l.igst || 0),
      0
    );
    return {
      subtotal: taxable + discount,
      discount,
      taxable,
      gst,
      grandTotal: taxable + gst,
    };
  }, [lines]);

  const handleMedicineSelect = (key, medicineId, option) => {
    patchRow(key, {
      medicine: medicineId,
      medicine_name: option?.label || "",
      batch: undefined,
      batches: [],
      loadingBatches: Boolean(medicineId),
    });
    if (!medicineId) return;
    getPharmacyMedicineBatchesApi(medicineId)
      .then((res) => {
        const list = res?.data?.results ?? res?.data ?? [];
        patchRow(key, {
          batches: Array.isArray(list) ? list : [],
          loadingBatches: false,
        });
      })
      .catch(() => patchRow(key, { batches: [], loadingBatches: false }));
  };

  const patchRow = (key, patch) => {
    setDirty(true);
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch, touched: true } : r))
    );
  };

  /*
   * Calling the return off.
   *
   * A draft can go at any time. A completed return has already taken the stock
   * out, so it can only be called off inside the day it was submitted - and a
   * Pharmacy Admin is not held to that.
   */
  const canCancel = canCancelDocument(entry, { isAdmin: isPharmacyAdmin });

  const handleCancelReturn = () => {
    Modal.confirm({
      title: "Cancel this purchase return?",
      content: "Are you sure? This cannot be undone.",
      okText: "Yes, cancel it",
      okButtonProps: { danger: true },
      cancelText: "No",
      /* Cancelling is done with the return, so the screen goes back to the
         list rather than sitting on a document nobody can do anything with. */
      onOk: () =>
        cancelPharmacyPurchaseReturnApi(entry.id)
          .then(() => {
            message.success("Purchase return cancelled");
            navigate(LIST_PATH);
          })
          .catch((err) =>
            message.error(cancelFailureMessage(err, getErrorMessage(err)))
          ),
    });
  };

  /*
   * Leaving.
   *
   * A return is written in one call when it is raised, so until then every
   * line and every field on the header is held in the browser and would go
   * with the screen. A saved return has nothing to lose and is let through
   * without a word.
   */
  /* Read when the question is asked rather than on every render - the form's
     own fields do not re-render this screen as they are filled in. */
  const hasUnsavedWork = () =>
    dirty || (!readOnly && (rows.length > 0 || Boolean(form.getFieldValue("vendor"))));

  const leaveScreen = () => {
    if (!hasUnsavedWork()) {
      navigate(LIST_PATH);
      return;
    }
    setLeaveOpen(true);
  };

  const saveAndLeave = async () => {
    /* A new return lands on the list as soon as it is raised; an edit is told
       to follow it there. A refusal leaves the reason on screen. */
    setLeaveOpen(false);
    await handleSave(true);
  };

  /* `leaveAfter` is set by the leave dialog's Save, which wants the list once
     the return is written rather than the return it just saved. */
  const handleSave = async (leaveAfter = false) => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!lines.length) {
      message.error("Add at least one product.");
      return;
    }

    setSaving(true);

    /* The header, in the shape both the create and the whole-return edit take. */
    const header = {
      vendor: values.vendor,
      return_date: values.return_date.format("YYYY-MM-DD"),
      reason: values.reason || undefined,
      subtotal: Number(totals.subtotal.toFixed(2)),
      gst_amount: Number(totals.gst.toFixed(2)),
      total_amount: Number(totals.grandTotal.toFixed(2)),
    };

    /* One line, in the shape every write takes it. */
    const itemPayload = (l) => ({
      batch: l.batch,
      qty: Number(l.qty || 0),
      rate: Number(l.rate || 0),
      gst_percentage: Number(l.gst_percent || 0),
      gst_amount: Number(l.sgst || 0) + Number(l.cgst || 0),
      amount: Number(l.amount || 0),
    });

    /*
     * Whether anything above the table was retyped. It decides how the return
     * is written: the header and the lines go up together or they do not, and
     * only the first needs the whole list sent.
     */
    const headerChanged =
      String(values.vendor ?? "") !== String(entry?.vendor ?? "") ||
      header.return_date !== (entry?.return_date || null) ||
      String(values.reason ?? "") !== String(entry?.reason ?? "");

    try {
      if (!entry?.id) {
        const res = await createPharmacyPurchaseReturnApi({
          ...header,
          items: lines.map(itemPayload),
        });
        const number = res?.data?.return_number;
        message.success(
          number ? `Purchase return ${number} created.` : "Purchase return created."
        );
        navigate(LIST_PATH);
        return;
      }

      let saved;
      if (headerChanged) {
        /* The header moved, so the whole return goes up at once - which is
           what `edit/` is for, and it takes the lines with it. */
        const res = await editPharmacyPurchaseReturnApi(entry.id, {
          ...header,
          items: lines.map(itemPayload),
        });
        saved = res?.data;
        setRemovedItemIds([]);
      } else {
        /*
         * Only the table moved, so only the lines that moved are sent - one
         * call each, naming the line. On a completed return the server moves
         * stock by the difference as each one lands, and refuses the ones it
         * cannot: a line sending back more than the batch still holds is
         * turned down by name rather than taking the whole save with it.
         */
        for (const itemId of removedItemIds) {
          // eslint-disable-next-line no-await-in-loop
          await removePharmacyPurchaseReturnEditItemApi(entry.id, itemId);
        }
        setRemovedItemIds([]);

        for (const line of lines) {
          if (!line.itemId) {
            // eslint-disable-next-line no-await-in-loop
            await addPharmacyPurchaseReturnEditItemApi(entry.id, itemPayload(line));
          } else if (line.touched) {
            // eslint-disable-next-line no-await-in-loop
            await updatePharmacyPurchaseReturnEditItemApi(
              entry.id,
              line.itemId,
              itemPayload(line)
            );
          }
        }

        /* The server recomputes the return's own figures as each line lands,
           so what it now holds is read back rather than guessed at. */
        const fresh = await getPharmacyPurchaseReturnApi(entry.id);
        saved = fresh?.data;
      }

      /* An edit stays on the return it changed, unless it was saved on the
         way out. */
      const current = saved || entry;
      setEntry(current);
      setRows((current?.items || []).map(itemToRow));
      setEditingKey(null);
      setRowBackup(null);
      setDirty(false);
      message.success(
        current?.return_number
          ? `Purchase return ${current.return_number} saved.`
          : "Changes saved."
      );
      if (leaveAfter) navigate(LIST_PATH);
    } catch (err) {
      message.error(
        purchaseFailureMessage(err, getErrorMessage(err), { returning: true })
      );
    } finally {
      setSaving(false);
    }
  };

  const runAction = (fn, done) => {
    setSaving(true);
    fn(returnId)
      .then((res) => {
        message.success(done);
        setEntry(res?.data || entry);
      })
      /* Completing a return is where "more than the stock" lands. */
      .catch((err) =>
        message.error(purchaseFailureMessage(err, getErrorMessage(err), { returning: true }))
      )
      .finally(() => setSaving(false));
  };

  /*
   * One set of columns, read or typed a line at a time. The line being edited
   * shows its controls; every other line shows its figures.
   */
  const columns = [
    {
      title: "Product",
      key: "medicine",
      width: "26%",
      render: (_, row) =>
        isEditingRow(row) ? (
          <AsyncSearchSelect
            fetcher={fetchActivePharmacyMedicines}
            placeholder="Search product"
            value={row.medicine}
            // A suggested line arrives as an id plus a name; without the
            // name seeded here the select would render the row blank.
            initialOption={
              row.medicine
                ? { value: row.medicine, label: row.medicine_name }
                : undefined
            }
            onChange={(value, option) =>
              handleMedicineSelect(row.key, value, option)
            }
          />
        ) : (
          row.medicine_name || "-"
        ),
    },
    {
      title: "Batch",
      key: "batch",
      width: "20%",
      render: (_, row) =>
        isEditingRow(row) ? (
          <Select
            placeholder={row.loadingBatches ? "Loading..." : "Select batch"}
            value={row.batch}
            loading={row.loadingBatches}
            disabled={!row.medicine || row.loadingBatches}
            onChange={(value) => patchRow(row.key, { batch: value })}
            options={(row.batches || []).map((b) => ({
              value: b.id,
              label: `${b.batch_number} · Qty ${b.available_qty}`,
            }))}
            notFoundContent="No stock in this facility"
            style={{ width: "100%" }}
          />
        ) : (
          row.batch_number || "-"
        ),
    },
    {
      title: "Qty",
      key: "qty",
      align: "right",
      width: "10%",
      render: (_, row) =>
        isEditingRow(row) ? (
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            disabled={!row.batch}
            value={row.qty}
            onChange={(value) => patchRow(row.key, { qty: value })}
          />
        ) : (
          row.qty
        ),
    },
    {
      title: "Rate",
      key: "rate",
      align: "right",
      width: "13%",
      render: (_, row) => {
        if (!isEditingRow(row)) return formatAmount(row.rate || 0);
        /* A return is credited at a rate, so nought credits nothing - said on
           the line rather than left to fail at the server. */
        const error = notPositiveError(row?.rate, "Rate");
        return (
          <>
            <InputNumber
              min={MONEY_MIN}
              precision={2}
              status={error ? "error" : undefined}
              style={{ width: "100%" }}
              value={row.rate}
              onChange={(value) => patchRow(row.key, { rate: value })}
            />
            {error ? (
              <Typography.Text
                type="danger"
                style={{ display: "block", fontSize: 11, lineHeight: 1.35, marginTop: 4 }}
              >
                {error}
              </Typography.Text>
            ) : null}
          </>
        );
      },
    },
    {
      title: "GST %",
      key: "gst_percent",
      align: "right",
      width: "11%",
      render: (_, row) =>
        isEditingRow(row) ? (
          <InputNumber
            min={0}
            max={100}
            style={{ width: "100%" }}
            value={row.gst_percent}
            onChange={(value) => patchRow(row.key, { gst_percent: value })}
          />
        ) : (
          `${row.gst_percent || 0}%`
        ),
    },
    {
      title: "Taxable",
      key: "taxable_amount",
      align: "right",
      width: "12%",
      render: (_, row) => formatAmount(lineFigures(row).taxable_amount),
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      width: "12%",
      render: (_, row) => formatAmount(lineFigures(row).amount),
    },
    {
      title: "",
      key: "actions",
      width: "10%",
      render: (_, row) => {
        if (!linesEditable) return null;

        /* A new return is typed line by line as it is built, so its lines
           only need taking off. A saved one is corrected a line at a time:
           the pencil opens one, the tick keeps what was typed, the cross puts
           it back. None of the three touches the server. */
        if (isNew) {
          return (
            <RowActions>
              <IconAction
                icon="trash"
                label="Remove line"
                tone="danger"
                onClick={() => removeRow(row.key)}
              />
            </RowActions>
          );
        }

        return (
          <RowActions>
            {editingKey === row.key ? (
              <>
                <IconAction
                  icon="check"
                  label="Keep these changes"
                  onClick={commitRowEdit}
                />
                <IconAction
                  icon="close"
                  label="Undo this line"
                  tone="danger"
                  onClick={revertRowEdit}
                />
              </>
            ) : (
              <>
                <IconAction
                  icon="edit"
                  label={`Edit ${row.medicine_name || "line"}`}
                  disabled={editingKey !== null}
                  onClick={() => startRowEdit(row)}
                />
                <IconAction
                  icon="trash"
                  label="Remove line"
                  tone="danger"
                  disabled={editingKey !== null}
                  onClick={() => removeRow(row.key)}
                />
              </>
            )}
          </RowActions>
        );
      },
    },
  ];

  /* A line added to a saved return opens for typing straight away - it has
     nothing in it, and nothing to read. */
  /*
   * Whether the bar at the foot has anything in it - see the purchase order
   * screen, which answers the same question the same way: nothing is offered
   * until the return is known, and a return with nothing left to do with it
   * gets no bar rather than one holding a lone way out.
   */
  const returnKnown = isNew || String(entry?.id) === String(returnId);
  const showFooter =
    returnKnown && (isNew || dirty || entry?.status === "draft" || canCancel);

  const addRow = () => {
    const row = newRow();
    setRows((prev) => [...prev, row]);
    /* No backup: a line that was just added has nothing to go back to, so the
       cross takes it off again. */
    setRowBackup(null);
    setEditingKey(row.key);
    setDirty(true);
  };

  const removeRow = (key) => {
    setRows((prev) => {
      const going = prev.find((r) => r.key === key);
      if (going?.itemId) {
        setRemovedItemIds((ids) =>
          ids.includes(going.itemId) ? ids : [...ids, going.itemId]
        );
      }
      return prev.filter((r) => r.key !== key);
    });
    if (editingKey === key) {
      setRowBackup(null);
      setEditingKey(null);
    }
    setDirty(true);
  };
  const lineCount = rows.length;

  return (
    <CustomLayout
      headerSearch={<PharmacyGlobalSearch />}
      layoutClassName="reception-layout compact-footer-layout pharmacy-motion"
      sideContent={<PharmacySideMenu />}
      header={
        // The mockup's header carries only search and the account menu; each
        // screen names itself in its own heading. Kept truthy so CustomLayout
        // still renders the bar.
        <></>
      }
      footer={
        !showFooter ? undefined : (
        <div style={footerActionsStyle}>
          {/*
            * Cancel calls the return off where there is a return to call off,
            * and is the way out before that - a return that was never raised
            * has nothing on the server to cancel. While it is being edited,
            * Discard changes is the way out of the edit.
            */}
          <UiButton
            variant="danger"
            onClick={canCancel && !dirty ? handleCancelReturn : leaveScreen}
          >
            Cancel
          </UiButton>
          {isNew ? (
            <UiButton loading={saving} onClick={() => handleSave()}>
              Create Purchase Return
            </UiButton>
          ) : dirty ? (
            /*
             * Nothing typed into the table has gone anywhere yet - the tick on
             * a line only closes it. This is where the return is written, in
             * one call, or put back to what the server holds.
             */
            <>
              <UiButton variant="secondary" disabled={saving} onClick={discardChanges}>
                Discard changes
              </UiButton>
              <UiButton
                loading={saving}
                disabled={editingKey !== null}
                onClick={() => handleSave()}
              >
                Save changes
              </UiButton>
            </>
          ) : (
            entry?.status === "draft" && (
              <UiButton
                loading={saving}
                onClick={() =>
                  runAction(
                    completePharmacyPurchaseReturnApi,
                    "Purchase return completed. Stock has been reduced."
                  )
                }
              >
                Complete Return
              </UiButton>
            )
          )}
        </div>
        )
      }
    >
      <div
        style={{
          padding: 24,
          paddingTop: 0,
          paddingBottom: showFooter ? 120 : 24,
        }}
      >
        <ListScreen>
          <DetailHead
            backLabel="Back to Purchase Returns"
            onBack={leaveScreen}
            title={entry?.return_number || "Raise Purchase Return"}
            meta={
              entry?.status ? (
                <UiTag tone={entry.status === "cancelled" ? "warning" : "success"} size="sm">
                  {entry.status.replaceAll("_", " ")}
                </UiTag>
              ) : null
            }
            actions={
              entry?.id && isPharmacyAdmin ? (
                <UiButton variant="secondary" onClick={() => setHistoryOpen(true)}>
                  <CgIcon name="ledger" size={16} stroke={2} />
                  Audit log
                </UiButton>
              ) : null
            }
          />

          <DetailGrid>
            <DetailFull>
              <DetailCard title="Return Header">
                <DetailBody>
                  <Form layout="vertical" form={form} disabled={readOnly}>
                    {/*
                      * What a return is raised against: the vendor the stock
                      * goes back to, the day it goes, and why it is going.
                      */}
                    <Row gutter={[14, 8]}>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item
                          label="Vendor"
                          name="vendor"
                          rules={[{ required: true, message: "Please select vendor" }]}
                        >
                          <AsyncSearchSelect
                            fetcher={fetchVendors}
                            loadOnOpen
                            placeholder="Search vendor"
                            initialOption={
                              entry?.vendor
                                ? { value: entry.vendor, label: entry.vendor_name }
                                : prefill?.vendor
                                ? { value: prefill.vendor, label: prefill.vendorName }
                                : undefined
                            }
                            onChange={(value) => form.setFieldValue("vendor", value)}
                            value={Form.useWatch("vendor", form)}
                            disabled={readOnly}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item
                          label="Return Date"
                          name="return_date"
                          initialValue={dayjs()}
                          rules={[{ required: true, message: "Please select the return date" }]}
                        >
                          <DatePicker
                            style={{ width: "100%" }}
                            format="DD MMM, YYYY"
                            disabled={readOnly}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item label="Reason" name="reason">
                          <Input
                            placeholder="Why the stock is going back"
                            disabled={readOnly}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </DetailBody>
              </DetailCard>
            </DetailFull>

            <DetailFull>
              <DetailCard
                title="Items"
                count={lineCount}
                /* Nothing to total and nothing to add to until the first batch
                   is picked - the empty state above offers that. */
                foot={
                  lineCount > 0 ? (
                    <FootTotal label="Return total" value={formatAmount(totals.grandTotal)}>
                      {linesEditable ? (
                        <AddLineButton onClick={addRow}>Add Item</AddLineButton>
                      ) : null}
                    </FootTotal>
                  ) : null
                }
                extra={
                  linesEditable ? (
                    <UiButton onClick={addRow}>
                      <PlusOutlined /> Add Item
                    </UiButton>
                  ) : null
                }
              >
                {lineCount === 0 ? (
                  <DetailEmpty
                    title="No products added"
                    hint={
                      'Click "Add Item" to start a line, then pick the product and the batch it is going back on.'
                    }
                  >
                    {linesEditable ? (
                      <AddLineButton onClick={addRow}>Add Item</AddLineButton>
                    ) : null}
                  </DetailEmpty>
                ) : (
                  <Table
                    className={listTableClass}
                    loading={loading}
                    rowKey={(row) => row.key || row.id}
                    columns={columns}
                    dataSource={rows}
                    pagination={false}
                    scroll={{ x: "max-content" }}
                  />
                )}
              </DetailCard>
            </DetailFull>

            <DetailCard title="GST Summary">
              <DetailBody>
                <GstSummaryTable items={lines} />
              </DetailBody>
            </DetailCard>

            <DetailCard title="Bill">
              <DetailBody>
                <BillTotals
                  rows={[
                    { label: "Subtotal", value: formatAmount(totals.subtotal) },
                    { label: "Discount", value: formatAmount(totals.discount) },
                    { label: "GST", value: formatAmount(totals.gst) },
                  ]}
                  totalLabel="Grand Total"
                  totalValue={formatAmount(totals.grandTotal)}
                />
                <CardNote>
                  A draft return does not change stock. Completing it reduces
                  stock for each batch and cannot be undone.
                </CardNote>
              </DetailBody>
            </DetailCard>
          </DetailGrid>
        </ListScreen>
      </div>

      <AuditLogDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        entityType={AUDIT_ENTITIES.purchaseReturn}
        entityId={entry?.id}
        documentNumber={entry?.return_number}
      />

      <LeaveWithoutSavingModal
        open={leaveOpen}
        saving={saving}
        title={
          entry?.id ? "Leave without saving?" : "Leave without raising this return?"
        }
        heading={
          entry?.id
            ? "These changes have not been saved"
            : "This return has not been raised"
        }
        detail={
          <>
            {rows.length} {rows.length === 1 ? "line" : "lines"} and the header
            above will be lost.{" "}
            {entry?.id
              ? "Saving writes them over the return as it stands."
              : "Raising the return writes it as a draft; stock only goes back when the return is completed."}
          </>
        }
        saveLabel={entry?.id ? "Save changes" : "Raise return"}
        onKeepEditing={() => setLeaveOpen(false)}
        onSave={saveAndLeave}
        onDiscard={() => navigate(LIST_PATH)}
      />
    </CustomLayout>
  );
};

export default PurchaseReturnPage;
